import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import api from "./api";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _messaging = null;
async function getMsg() {
  if (!config.apiKey || !(await isSupported().catch(() => false))) return null;
  const app = getApps()[0] || initializeApp(config);
  if (!_messaging) _messaging = getMessaging(app);
  return _messaging;
}

export const pushSupported = () =>
  typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;

export const pushPermission = () => (pushSupported() ? Notification.permission : "unsupported");

export const pushEnabled = () =>
  pushSupported() && pushPermission() === "granted" && !!localStorage.getItem("mc_fcm_token");

// Request permission, get FCM token, save to backend. Returns true on success.
export async function enablePush() {
  if (!pushSupported()) throw new Error("Push notifications aren't supported on this device/browser.");
  const messaging = await getMsg();
  if (!messaging) throw new Error("Push notifications aren't available here.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were not enabled.");
  const swReg = (await navigator.serviceWorker.getRegistration("/")) || (await navigator.serviceWorker.register("/service-worker.js"));
  await navigator.serviceWorker.ready;
  const token = await getToken(messaging, { vapidKey: VAPID, serviceWorkerRegistration: swReg });
  if (!token) throw new Error("Could not get a notification token.");
  await api.post("/push/subscribe", { token });
  localStorage.setItem("mc_fcm_token", token);
  return true;
}

export async function disablePush() {
  const token = localStorage.getItem("mc_fcm_token");
  if (token) { try { await api.post("/push/unsubscribe", { token }); } catch (e) { console.debug("push unsubscribe failed", e?.message); } }
  localStorage.removeItem("mc_fcm_token");
}

// ---- App icon badge (Badging API) — the count on the home-screen icon ----
export function setAppBadge(count) {
  const n = Number(count) || 0;
  try {
    if ("setAppBadge" in navigator) {
      if (n > 0) navigator.setAppBadge(n);
      else if (navigator.clearAppBadge) navigator.clearAppBadge();
    }
  } catch { /* ignore */ }
  // Keep the service worker's background counter in sync (so background pushes continue from here).
  try { navigator.serviceWorker?.controller?.postMessage({ type: "sync-badge", count: n }); } catch { /* ignore */ }
}

export function clearAppBadge() { setAppBadge(0); }

// Show foreground messages as a toast.
export async function initForegroundMessages(onToast) {
  const messaging = await getMsg();
  if (!messaging) return;
  onMessage(messaging, (payload) => {
    const t = payload.notification?.title || payload.data?.title || "MissionCompanion";
    const b = payload.notification?.body || payload.data?.body || "";
    onToast?.(t, b, payload.data?.url);
  });
}
